import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingCart, FiStar, FiHeart } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import wishlistService from '../../services/wishlist.service';
import { toast } from 'react-toastify';

const ProductCard = ({ product, isWishlisted: initialWishlisted = false }) => {
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [wishlisted, setWishlisted] = useState(initialWishlisted);
  const [wishLoading, setWishLoading] = useState(false);
  const primaryImage = product.images?.find((img) => img.isPrimary) || product.images?.[0];

  const handleAddToCart = async (e) => {
    e.preventDefault();
    try {
      await addToCart(product._id);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Please login to save items');
      return;
    }
    setWishLoading(true);
    try {
      if (wishlisted) {
        await wishlistService.remove(product._id);
        setWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistService.add(product._id);
        setWishlisted(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      // If already wishlisted on add, just toggle UI
      if (error.response?.status === 400) {
        setWishlisted(true);
      } else {
        toast.error('Failed to update wishlist');
      }
    } finally {
      setWishLoading(false);
    }
  };

  return (
    <div className="product-card h-100">
      <Link to={`/products/${product.slug || product._id}`}>
        <div className="position-relative">
          {primaryImage ? (
            <img
              src={primaryImage.url}
              alt={primaryImage.alt || product.name}
              className="product-image"
              onError={(e) => {
                e.target.onerror = null;
                const color = product.category?.includes('Veg') ? '27AE60' : product.category?.includes('Fruit') ? 'F39C12' : '34495E';
                e.target.src = `https://placehold.co/600x600/${color}/ffffff?text=${encodeURIComponent(product.name?.split(' ').slice(0, 2).join('+') || 'Product')}`;
              }}
            />
          ) : (
            <div className="product-image d-flex align-items-center justify-content-center bg-light">
              <span className="text-muted">No Image</span>
            </div>
          )}

          {/* Wishlist heart */}
          <button
            className="btn btn-sm position-absolute border-0 p-0 d-flex align-items-center justify-content-center"
            style={{
              top: '10px',
              right: '10px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(4px)',
              transition: 'transform 0.2s',
              zIndex: 2,
            }}
            onClick={toggleWishlist}
            disabled={wishLoading}
            title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <FiHeart
              size={16}
              style={{
                fill: wishlisted ? '#B91C1C' : 'none',
                stroke: wishlisted ? '#B91C1C' : '#6B7280',
                transition: 'all 0.2s',
              }}
            />
          </button>

          {/* Discount badge */}
          {product.compareAtPrice && product.compareAtPrice > product.price && (
            <span
              className="position-absolute badge"
              style={{
                top: '10px',
                left: '10px',
                background: '#B91C1C',
                color: '#fff',
                fontSize: '0.7rem',
              }}
            >
              -{Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}%
            </span>
          )}
        </div>

        <div className="product-body">
          <p className="product-category mb-1">{product.category}</p>
          <h6 className="product-title">{product.name}</h6>

          {product.ratings?.count > 0 && (
            <div className="product-rating mb-2">
              <FiStar size={14} className="me-1" />
              {product.ratings.average.toFixed(1)} ({product.ratings.count})
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between">
            <div className="product-price">
              ${product.price?.toFixed(2)}
              {product.compareAtPrice && product.compareAtPrice > product.price && (
                <span className="original-price">${product.compareAtPrice.toFixed(2)}</span>
              )}
            </div>

            <button
              className="btn btn-sm btn-fd-primary"
              onClick={handleAddToCart}
              title="Add to cart"
            >
              <FiShoppingCart size={16} />
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
